import React, {useState, useCallback, useRef} from "react";
import {
  TrackChangesDialog,
  TrackChangesErrorDialog,
} from "common_components/track_changes_dialogs";
import {getIsTrackAllChangesOn as getIsTrackAllChangesOnDirect} from "word/taskpane/routes/checklist/utils/get_check_tracking_mode";
import {enableTrackAllChanges as enableTrackAllChangesDirect} from "word/taskpane/routes/checklist/utils/enable_track_changes_all";

export function useTrackChangesCheck(
  getIsTrackAllChangesOn: () => Promise<boolean> = getIsTrackAllChangesOnDirect,
  enableTrackAllChanges: () => Promise<void> = enableTrackAllChangesDirect,
) {
  const [showTrackChangesDialog, setShowTrackChangesDialog] = useState(false);
  const [
    showTrackChangesErrorDialog,
    setShowTrackChangesErrorDialog,
  ] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const pendingApplyRef = useRef<(() => Promise<void>) | null>(null);

  const resetPending = useCallback(() => setIsPending(false), []);

  const runPendingApply = useCallback(async () => {
    const applyFn = pendingApplyRef.current;
    pendingApplyRef.current = null;
    if (applyFn) {
      try {
        await applyFn();
      } catch {
        setIsPending(false);
      }
    } else {
      setIsPending(false);
    }
  }, []);

  const checkTrackChangesBeforeApply = useCallback(
    async (applyFn: () => Promise<void>) => {
      setIsPending(true);
      let isOn = false;
      try {
        isOn = await getIsTrackAllChangesOn();
      } catch {
        // default to track changes off
      }
      if (isOn) {
        pendingApplyRef.current = applyFn;
        await runPendingApply();
      } else {
        pendingApplyRef.current = applyFn;
        setShowTrackChangesDialog(true);
      }
    },
    [getIsTrackAllChangesOn, runPendingApply],
  );

  const handleCancel = useCallback(() => {
    pendingApplyRef.current = null;
    setIsPending(false);
    setShowTrackChangesDialog(false);
  }, []);

  const handleApplyWithoutTracking = useCallback(async () => {
    setShowTrackChangesDialog(false);
    setShowTrackChangesErrorDialog(false);
    await runPendingApply();
  }, [runPendingApply]);

  const handleEnableAndApply = useCallback(async () => {
    setShowTrackChangesDialog(false);
    try {
      await enableTrackAllChanges();
    } catch {
      setShowTrackChangesErrorDialog(true);
      return;
    }
    await runPendingApply();
  }, [enableTrackAllChanges, runPendingApply]);

  const trackChangesDialogs = (
    <>
      <TrackChangesDialog
        open={showTrackChangesDialog}
        onClose={handleCancel}
        onApplyWithoutTracking={handleApplyWithoutTracking}
        onConfirm={handleEnableAndApply}
      />
      <TrackChangesErrorDialog
        open={showTrackChangesErrorDialog}
        onClose={() => {
          pendingApplyRef.current = null;
          setIsPending(false);
          setShowTrackChangesErrorDialog(false);
        }}
        onApplyWithoutTracking={handleApplyWithoutTracking}
      />
    </>
  );

  return {
    checkTrackChangesBeforeApply,
    trackChangesDialogs,
    isPending,
    resetPending,
  };
}
